// src/components/ui/Button.tsx - ИСПРАВЛЕННАЯ версия без prop warnings
import React from 'react';
import styled, { css } from 'styled-components';
import { motion } from 'framer-motion';

interface ButtonProps {
  children: React.ReactNode;
  variant?: 'primary' | 'secondary' | 'outline' | 'ghost' | 'danger';
  size?: 'small' | 'medium' | 'large';
  fullWidth?: boolean;
  disabled?: boolean;
  loading?: boolean;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;
  type?: 'button' | 'submit' | 'reset';
  className?: string;
  style?: React.CSSProperties;
}

// ✅ ИСПРАВЛЕНО: Используем transient props с $
interface StyledButtonProps {
  $variant: ButtonProps['variant'];
  $size: ButtonProps['size'];
  $fullWidth: boolean;
  $loading: boolean;
}

// Стили для различных вариантов кнопок
const variantStyles = {
  primary: css`
    background: var(--tg-theme-button-color, var(--gradient-purple));
    color: var(--tg-theme-button-text-color, #FFFFFF);
    border: none;
    box-shadow: 0 4px 15px rgba(123, 104, 238, 0.25);

    &:hover:not(:disabled) {
      background: linear-gradient(135deg, #6A5ACD, #8855BB);
      transform: translateY(-1px);
      box-shadow: 0 6px 20px rgba(123, 104, 238, 0.35);
    }

    &:active:not(:disabled) {
      transform: translateY(0);
    }

    [data-theme="dark"] & {
      box-shadow: 0 4px 15px rgba(157, 142, 238, 0.3);

      &:hover:not(:disabled) {
        background: linear-gradient(135deg, #B8A4FF, #C896EC);
        box-shadow: 0 6px 20px rgba(157, 142, 238, 0.4);
      }
    }
  `,

  secondary: css`
    background: var(--tg-theme-secondary-bg-color, var(--surface-light));
    color: var(--tg-theme-text-color, var(--text-primary));
    border: 1px solid var(--border-medium);

    &:hover:not(:disabled) {
      background: var(--surface-medium);
      border-color: var(--border-strong);
      transform: translateY(-1px);
    }
  `,

  outline: css`
    background: transparent;
    color: var(--tg-theme-button-color, #7B68EE);
    border: 2px solid var(--tg-theme-button-color, #7B68EE);

    &:hover:not(:disabled) {
      background: var(--tg-theme-button-color, #7B68EE);
      color: var(--tg-theme-button-text-color, #FFFFFF);
      transform: translateY(-1px);
    }

    [data-theme="dark"] & {
      color: #C4B5FD;
      border-color: #C4B5FD;

      &:hover:not(:disabled) {
        background: #C4B5FD;
        color: #FFFFFF;
      }
    }
  `,

  ghost: css`
    background: transparent;
    color: var(--tg-theme-text-color, var(--text-primary));
    border: none;

    &:hover:not(:disabled) {
      background: var(--tg-theme-secondary-bg-color, var(--surface-light));
      transform: translateY(-1px);
    }
  `,

  danger: css`
    background: #EF4444;
    color: #FFFFFF;
    border: none;

    &:hover:not(:disabled) {
      background: #DC2626;
      transform: translateY(-1px);
      box-shadow: 0 4px 15px rgba(239, 68, 68, 0.25);
    }

    [data-theme="dark"] & {
      background: #F87171;

      &:hover:not(:disabled) {
        background: #EF4444;
      }
    }
  `,
};

// Стили для размеров
const sizeStyles = {
  small: css`
    padding: 8px 16px;
    font-size: 14px;
    min-height: 36px;
  `,
  
  medium: css`
    padding: 12px 24px;
    font-size: 16px;
    min-height: 44px;
  `,
  
  large: css`
    padding: 16px 32px;
    font-size: 18px;
    min-height: 52px;
  `,
};

// ✅ ИСПРАВЛЕНО: Блокируем передачу нестандартных props
const StyledButton = styled(motion.button).withConfig({
  shouldForwardProp: (prop) => !['$variant', '$size', '$fullWidth', '$loading'].includes(prop),
})<StyledButtonProps>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  border-radius: 16px;
  font-weight: 600;
  font-family: inherit;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  outline: none;
  position: relative;
  overflow: hidden;
  user-select: none;
  
  /* Применяем стили варианта */
  ${props => props.$variant && variantStyles[props.$variant]}
  
  /* Применяем стили размера */
  ${props => props.$size && sizeStyles[props.$size]}
  
  /* Полная ширина */
  ${props => props.$fullWidth && css`
    width: 100%;
  `}
  
  /* Состояние отключения */
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none !important;
  }
  
  /* Состояние загрузки */
  ${props => props.$loading && css`
    pointer-events: none;
    opacity: 0.8;
  `}
  
  /* Фокус для доступности */
  &:focus-visible {
    outline: 2px solid var(--tg-theme-button-color, #7B68EE);
    outline-offset: 2px;
  }
  
  /* Ripple эффект */
  &::before {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    width: 0;
    height: 0;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.3);
    transform: translate(-50%, -50%);
    transition: width 0.3s, height 0.3s;
  }
  
  &:active:not(:disabled)::before {
    width: 200px;
    height: 200px;
  }
`;

const LoadingSpinner = styled(motion.div)`
  width: 16px;
  height: 16px;
  border: 2px solid transparent;
  border-top: 2px solid currentColor;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  
  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

const IconWrapper = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  
  svg {
    width: 16px;
    height: 16px;
  }
`;

export const Button: React.FC<ButtonProps> = ({
  children,
  variant = 'primary',
  size = 'medium',
  fullWidth = false,
  disabled = false,
  loading = false,
  leftIcon,
  rightIcon,
  onClick,
  type = 'button',
  className,
  style,
  ...props
}) => {
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (loading || disabled) return;
    onClick?.(event);
  };

  return (
    <StyledButton
      $variant={variant}
      $size={size}
      $fullWidth={fullWidth}
      $loading={loading}
      disabled={disabled || loading}
      onClick={handleClick}
      type={type}
      className={className}
      style={style}
      whileHover={{ scale: disabled || loading ? 1 : 1.02 }}
      whileTap={{ scale: disabled || loading ? 1 : 0.98 }}
      transition={{ type: "spring", stiffness: 400, damping: 25 }}
      {...props}
    >
      {/* Левая иконка */}
      {leftIcon && !loading && (
        <IconWrapper>{leftIcon}</IconWrapper>
      )}
      
      {/* Спиннер загрузки */}
      {loading && (
        <LoadingSpinner
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.2 }}
        />
      )}
      
      {/* Текст кнопки */}
      <span>{children}</span>
      
      {/* Правая иконка */}
      {rightIcon && !loading && (
        <IconWrapper>{rightIcon}</IconWrapper>
      )}
    </StyledButton>
  );
};

// =============================================================================
// src/components/ui/Card.tsx - ИСПРАВЛЕННАЯ версия без prop warnings
// =============================================================================

interface CardProps {
  children: React.ReactNode;
  variant?: 'default' | 'elevated' | 'outline' | 'glass';
  padding?: 'none' | 'small' | 'medium' | 'large';
  className?: string;
  onClick?: () => void;
  hoverable?: boolean;
  as?: React.ElementType;
  style?: React.CSSProperties;
}

// ✅ Интерфейс для styled props
interface StyledCardProps {
  $variant: CardProps['variant'];
  $padding: CardProps['padding'];
  $clickable: boolean;
  $hoverable: boolean;
}

// Стили для различных вариантов карточек
const cardVariantStyles = {
  default: css`
    background: var(--tg-theme-secondary-bg-color, linear-gradient(145deg, rgba(255,255,255,0.95), rgba(255,255,255,0.85)));
    backdrop-filter: blur(20px);
    border: 1px solid var(--tg-theme-secondary-bg-color, rgba(255, 255, 255, 0.2));
    box-shadow: 0 4px 20px rgba(123, 104, 238, 0.1);
  `,
  
  elevated: css`
    background: var(--tg-theme-secondary-bg-color, #FFFFFF);
    border: none;
    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1), 0 4px 10px rgba(0, 0, 0, 0.05);
  `,
  
  outline: css`
    background: transparent;
    border: 2px solid var(--tg-theme-secondary-bg-color, rgba(123, 104, 238, 0.2));
    box-shadow: none;
  `,
  
  glass: css`
    background: linear-gradient(145deg, rgba(255,255,255,0.1), rgba(255,255,255,0.05));
    border: 1px solid rgba(255, 255, 255, 0.2);
    backdrop-filter: blur(20px);
    -webkit-backdrop-filter: blur(20px);
    box-shadow: 0 8px 32px rgba(31, 38, 135, 0.37);
  `,
};

// Стили для отступов
const cardPaddingStyles = {
  none: css`
    padding: 0;
  `,
  
  small: css`
    padding: 12px;
  `,
  
  medium: css`
    padding: 20px;
  `,
  
  large: css`
    padding: 24px;
  `,
};

// ✅ ИСПРАВЛЕНО: Блокируем передачу нестандартных props
const StyledCard = styled(motion.div).withConfig({
  shouldForwardProp: (prop) => !['$variant', '$padding', '$clickable', '$hoverable'].includes(prop),
})<StyledCardProps>`
  border-radius: 20px;
  transition: all 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  position: relative;
  overflow: hidden;
  width: 100%;
  
  /* Применяем стили варианта */
  ${props => props.$variant && cardVariantStyles[props.$variant]}
  
  /* Применяем стили отступов */
  ${props => props.$padding && cardPaddingStyles[props.$padding]}
  
  /* Стили для кликабельных карточек */
  ${props => props.$clickable && css`
    cursor: pointer;
    user-select: none;
    
    &:active {
      transform: scale(0.98);
    }
  `}
  
  /* Стили для ховерабельных карточек */
  ${props => props.$hoverable && css`
    &:hover {
      transform: translateY(-4px);
      box-shadow: 0 20px 40px rgba(123, 104, 238, 0.15);
    }
  `}
  
  /* Фокус для доступности */
  ${props => props.$clickable && css`
    &:focus-visible {
      outline: 2px solid var(--tg-theme-button-color, #7B68EE);
      outline-offset: 2px;
    }
  `}
  
  /* Современный border gradient */
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: linear-gradient(90deg, #7B68EE, #87CEEB);
    opacity: 0;
    transition: opacity 0.4s ease;
  }
  
  &:hover::before {
    opacity: ${props => props.$hoverable ? 1 : 0};
  }
`;

export const Card: React.FC<CardProps> = ({
  children,
  variant = 'default',
  padding = 'medium',
  className,
  onClick,
  hoverable = true,
  as,
  style,
  ...props
}) => {
  const isClickable = !!onClick;

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onClick?.();
    }
  };

  return (
    <StyledCard
      $variant={variant}
      $padding={padding}
      $clickable={isClickable}
      $hoverable={hoverable}
      className={className}
      onClick={onClick}
      role={isClickable ? 'button' : undefined}
      tabIndex={isClickable ? 0 : undefined}
      onKeyDown={isClickable ? handleKeyDown : undefined}
      whileHover={hoverable ? { y: -4 } : undefined}
      whileTap={isClickable ? { scale: 0.98 } : undefined}
      transition={{ type: "spring", stiffness: 400, damping: 25 }}
      as={as}
      style={style}
      {...props}
    >
      {children}
    </StyledCard>
  );
};

// Дополнительные компоненты для структуры карточки
export const CardHeader = styled.div`
  margin-bottom: 16px;
`;

export const CardTitle = styled.h3`
  font-size: 18px;
  font-weight: 700;
  color: var(--tg-theme-text-color, #000000);
  margin: 0 0 4px 0;
  line-height: 1.3;
`;

export const CardSubtitle = styled.p`
  font-size: 16px;
  color: var(--tg-theme-hint-color, rgba(0,0,0,0.6));
  margin: 0;
  line-height: 1.4;
`;

export const CardContent = styled.div`
  color: var(--tg-theme-text-color, #000000);
  line-height: 1.5;
`;

export const CardFooter = styled.div`
  margin-top: 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
`;

export const CardActions = styled.div`
  display: flex;
  gap: 8px;
  margin-left: auto;
`;

// =============================================================================
// src/components/ui/Input.tsx - ИСПРАВЛЕННАЯ версия без prop warnings
// =============================================================================

interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  error?: boolean;
  fullWidth?: boolean;
  label?: string;
  helperText?: string;
}

// ✅ Интерфейс для styled props
interface StyledInputProps {
  $error: boolean;
  $fullWidth: boolean;
}

// ✅ ИСПРАВЛЕНО: Блокируем передачу нестандартных props
const StyledInput = styled.input.withConfig({
  shouldForwardProp: (prop) => !['$error', '$fullWidth'].includes(prop),
})<StyledInputProps>`
  width: ${props => props.$fullWidth ? '100%' : 'auto'};
  padding: 14px 18px;
  border: 2px solid var(--tg-theme-secondary-bg-color, rgba(123, 104, 238, 0.1));
  border-radius: 16px;
  font-size: 16px;
  color: var(--tg-theme-text-color, #000000);
  background: var(--tg-theme-bg-color, linear-gradient(145deg, rgba(255,255,255,0.95), rgba(255,255,255,0.85)));
  backdrop-filter: blur(20px);
  transition: all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  box-shadow: 0 4px 20px rgba(123, 104, 238, 0.05);
  
  &::placeholder {
    color: var(--tg-theme-hint-color, rgba(0,0,0,0.5));
  }
  
  &:focus {
    outline: none;
    border-color: var(--tg-theme-button-color, #7B68EE);
    box-shadow: 
      0 4px 20px rgba(123, 104, 238, 0.1),
      0 0 0 3px rgba(123, 104, 238, 0.1);
    transform: translateY(-1px);
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    background: var(--tg-theme-secondary-bg-color, rgba(0,0,0,0.05));
  }
  
  ${props => props.$error && css`
    border-color: #EF4444;
    &:focus {
      border-color: #EF4444;
      box-shadow: 
        0 4px 20px rgba(239, 68, 68, 0.1),
        0 0 0 3px rgba(239, 68, 68, 0.1);
    }
  `}
`;

const InputWrapper = styled.div<{ $fullWidth: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: ${props => props.$fullWidth ? '100%' : 'auto'};
`;

const InputLabel = styled.label`
  font-size: 14px;
  font-weight: 600;
  color: var(--tg-theme-text-color, #000000);
`;

const HelperText = styled.p<{ $error: boolean }>`
  font-size: 12px;
  margin: 0;
  color: ${props => props.$error 
    ? '#EF4444' 
    : 'var(--tg-theme-hint-color, rgba(0,0,0,0.6))'
  };
`;

export const Input: React.FC<InputProps> = ({ 
  error = false, 
  fullWidth = false,
  label,
  helperText,
  className,
  ...props 
}) => {
  const content = (
    <StyledInput 
      $error={error} 
      $fullWidth={fullWidth}
      className={className}
      {...props} 
    />
  );

  if (label || helperText) {
    return (
      <InputWrapper $fullWidth={fullWidth}>
        {label && <InputLabel>{label}</InputLabel>}
        {content}
        {helperText && <HelperText $error={error}>{helperText}</HelperText>}
      </InputWrapper>
    );
  }

  return content;
};